"use client";

import React from "react";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
import { cn } from "@/core/lib/utils";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { Heading } from "@/components/ui/Heading";
import ProductBreadcrumb from "./ProductBreadcrumb";
import { ProductSortSidebar } from "./ProductSort";
import AvailabilityFilterProduct from "./AvailabilityFilterProduct";
import { Navigation } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CustomButton = loadThemeComponent("CustomButton");

interface Category {
  label: string;
  value: string;
}

interface CategoryNavProps {
  categories: Category[];
  activeSlug: string;
}

function CategoryNavComponent({ categories, activeSlug }: CategoryNavProps) {
  const { dictionary } = useDictionary();

  const allCategories = React.useMemo(
    () => [{ label: dictionary.ui.profile.all, value: "all" }, ...categories],
    [categories, dictionary]
  );

  const activeCategory = allCategories.find(c => c.value === activeSlug);

  return (
    <div className="mt-7 mb-5">
      <ProductBreadcrumb
        activeLabel={activeCategory?.label || ""}
        activeSlug={activeSlug}
      />

      {/* {activeCategory && <Heading level={2}>{activeCategory.label}</Heading>} */}


      <div className="relative my-8">

        <CustomButton className="category-next absolute left-0 top-1/2 -translate-y-1/2 z-10  w-8 h-8 rounded-full border border-primary-900 bg-white flex items-center justify-center">
          <ChevronLeft className="w-4.5 h-4.5 stroke-1" />
        </CustomButton>

        <CustomButton className="category-prev absolute right-0 top-1/2 -translate-y-1/2 z-10  w-8 h-8 rounded-full border border-primary-900 bg-white flex items-center justify-center">
          <ChevronRight className="w-4.5 h-4.5 stroke-1" />
        </CustomButton>

        <Swiper
          modules={[Navigation]}
          slidesPerView="auto"
          spaceBetween={16}
          navigation={{
            prevEl: ".category-prev",
            nextEl: ".category-next",
          }}
          className="px-10!"
        >
          {allCategories.map(({ label, value }) => {
            const isActive = activeSlug === value

            return (
              <SwiperSlide key={value} className="w-auto!">
                <Link
                  href={`/shop/category/products/${value}`}
                  className={cn(
                    "px-4 py-2 text-sm whitespace-nowrap border-b-2 transition",
                    isActive
                      ? "border-primary-900 text-primary-900 font-primary-bold"
                      : "border-transparent text-primary-600 hover:text-primary-900"
                  )}
                >
                  {label}
                </Link>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>

      <div className="flex justify-between p-4 bg-primary-0 ">
        <ProductSortSidebar />
        <AvailabilityFilterProduct />
      </div>
    </div>
  );
}

export const CategoryNav = React.memo(CategoryNavComponent);
